import type { UUID } from '@cmg/common';
import { Button, Grid, LoadingButton, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import { Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRoutes } from '../../../../../common/hooks/useRoutes/useRoutes';
import { GQLExtensionErrorAlert } from '../../../../../design-system/alert/GQLExtensionErrorAlert';
import { IdentityPageContent } from '../../../../../design-system/IdentityPageContent';
import type { InvestorFirmCreateDtoInput } from '../../../../../graphql';
import { RolodexRecordStatus } from '../../../../../graphql';
import { LocalHighlights } from '../../crm-sales-coverage/components/LocalHighlights';
import { ContactInformationFields } from './components/ContactInformationFields';
import { FirmDetailsFields } from './components/FirmDetailsFields';
import { useCreateInvestorFirm } from './hooks/useCreateInvestorFirm';
import {
  getInvestorFirmDetailInitialValues,
  newInvestorFirmValidationSchema,
} from './models/InvestorFirmDetail.model';

export function CMRInvestorFirmCreateRoute() {
  const navigate = useNavigate();

  const { crmIntegrationId } = useParams<{ crmIntegrationId?: UUID }>();

  const { crmInvestorFirms } = useRoutes(crmIntegrationId);

  const redirectUrlToInvestorFirms = useMemo(() => {
    return crmInvestorFirms.getUrlPath({ crmIntegrationId });
  }, [crmInvestorFirms, crmIntegrationId]);

  const { addInvestorFirm, error } = useCreateInvestorFirm(crmIntegrationId);

  const formik = useFormik<InvestorFirmCreateDtoInput>({
    initialValues: getInvestorFirmDetailInitialValues(),
    validationSchema: newInvestorFirmValidationSchema,
    onSubmit: async values => {
      try {
        await addInvestorFirm({
          ...values,
          status: RolodexRecordStatus.Effective,
        });

        SnackbarManager.success('Investor Firm created successfully.');
        navigate(crmInvestorFirms.getUrlPath({ crmIntegrationId }));
      } catch {
        SnackbarManager.error('Failed to create Investor Firm.');
      }
    },
  });

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Investor Firms',
        href: redirectUrlToInvestorFirms,
      },
      { label: 'New Investor Firm' },
    ],
    [redirectUrlToInvestorFirms]
  );

  const handleCancel = useCallback(() => {
    navigate(redirectUrlToInvestorFirms);
  }, [navigate, redirectUrlToInvestorFirms]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  const actions = useMemo(
    () => [
      <Button key="cancel-button" variant="outlined" onClick={handleCancel}>
        Cancel
      </Button>,
      <LoadingButton
        key="save-button"
        variant="contained"
        loading={formik.isSubmitting}
        onClick={handleSave}
      >
        Save
      </LoadingButton>,
    ],
    [handleCancel, handleSave, formik.isSubmitting]
  );

  return (
    <FormikProvider value={formik}>
      <IdentityPageContent
        localHighlights={
          <LocalHighlights
            title="New Investor Firm"
            breadcrumbs={breadcrumbs}
            actions={actions}
            errors={<GQLExtensionErrorAlert title="Failed to create Investor Firm" error={error} />}
          />
        }
        gridContent={
          <Fragment>
            <Grid item xs={12} md={6}>
              <Section title="Firm Details">
                <FirmDetailsFields />
              </Section>
            </Grid>
            <Grid item xs={12} md={6}>
              <Section title="Contact Information">
                <ContactInformationFields />
              </Section>
            </Grid>
          </Fragment>
        }
      />
    </FormikProvider>
  );
}
